import React, { useEffect, useMemo, useState } from 'react';
import MyButton from '../../../components/MyButton';
import MyCheckBox from '../../../components/MyCheckbox';
import { FIELD_TYPES, OFFER_FIELDS, isFieldLocked } from '../constants';
import OfferFieldEditor from './OfferFieldEditor';

function emptyValueForField(field) {
  switch (field.type) {
    case FIELD_TYPES.subCategories:
    case FIELD_TYPES.additionalCities:
    case FIELD_TYPES.languages:
    case FIELD_TYPES.tags:
    case FIELD_TYPES.templatePhotos:
    case FIELD_TYPES.photoUrls:
      return [];
    case FIELD_TYPES.category:
    case FIELD_TYPES.city:
    case FIELD_TYPES.coordinates:
      return null;
    case FIELD_TYPES.schedule:
      return {};
    case FIELD_TYPES.isForAllLanguages:
      return true;
    default:
      return '';
  }
}

/**
 * Верхняя панель: выбор карточек, пакетное создание/удаление и правка одного поля у выбранных.
 */
function BulkToolbar({
  stats,
  selectedDrafts,
  config,
  categories,
  countries,
  languages,
  creatingProgress,
  onSelectAll,
  onSelectNone,
  onCreateSelected,
  onRemoveSelected,
  onRemoveCreated,
  onApplyToSelected,
}) {
  const editableFields = useMemo(
    () => OFFER_FIELDS.filter((field) => !isFieldLocked(field, config)),
    [config],
  );
  const lockedFields = useMemo(
    () => OFFER_FIELDS.filter((field) => isFieldLocked(field, config)),
    [config],
  );

  const [fieldKey, setFieldKey] = useState('');
  const [value, setValue] = useState('');
  const [extra, setExtra] = useState(null);
  const [randomPhotos, setRandomPhotos] = useState(true);
  const [photosPerOffer, setPhotosPerOffer] = useState(config.photosPerOffer || 1);

  const field = useMemo(
    () => editableFields.find((item) => item.key === fieldKey) || null,
    [editableFields, fieldKey],
  );

  useEffect(() => {
    setValue(field ? emptyValueForField(field) : '');
    setExtra(null);
  }, [fieldKey]);

  const sharedCategory = useMemo(() => {
    if (!selectedDrafts.length) return null;
    const first = selectedDrafts[0].category;
    const same = selectedDrafts.every((draft) => draft.category?._id === first?._id);
    return same ? first : null;
  }, [selectedDrafts]);

  const sharedCountryId = useMemo(() => {
    if (!selectedDrafts.length) return config.country?._id || '';
    const first = selectedDrafts[0].city?.country_id || '';
    const same = selectedDrafts.every((draft) => (draft.city?.country_id || '') === first);
    return same ? first : (config.country?._id || '');
  }, [selectedDrafts, config]);

  const canApply = Boolean(field) && stats.selected > 0;

  const handleApply = () => {
    if (!canApply) return;
    const options = field.type === FIELD_TYPES.templatePhotos
      ? { randomPhotos, photosPerOffer }
      : null;
    onApplyToSelected(field, value, extra, options);
  };

  return (
    <div className="bulk_offers_toolbar">
      <div className="bulk_offers_toolbar_top">
        <div className="bulk_offers_stats">
          <span>{`Всего: ${stats.total}`}</span>
          <span>{`Выбрано: ${stats.selected}`}</span>
          <span className="ready">{`Готово: ${stats.ready}`}</span>
          <span className="invalid">{`С ошибками: ${stats.invalid}`}</span>
          <span className="created">{`Создано: ${stats.created}`}</span>
        </div>

        <div className="bulk_offers_toolbar_buttons">
          <MyButton text="Выбрать все" onClick={onSelectAll} secondary className="bulk_offers_toolbar_button" />
          <MyButton text="Снять выбор" onClick={onSelectNone} secondary className="bulk_offers_toolbar_button" />
          <MyButton
            text="Создать выбранные"
            onClick={onCreateSelected}
            disabled={!stats.selected}
            loading={Boolean(creatingProgress)}
            className="bulk_offers_toolbar_button"
          />
          <MyButton
            text="Убрать выбранные"
            onClick={onRemoveSelected}
            disabled={!stats.selected}
            secondary
            className="bulk_offers_toolbar_button bulk_offers_toolbar_button_danger"
          />
          <MyButton
            text="Убрать созданные"
            onClick={onRemoveCreated}
            disabled={!stats.created}
            secondary
            className="bulk_offers_toolbar_button"
          />
        </div>
      </div>

      {creatingProgress ? (
        <p className="bulk_offers_toolbar_progress">{creatingProgress}</p>
      ) : null}

      <div className="bulk_offers_toolbar_apply">
        <div className="bulk_offers_toolbar_apply_head">
          <p className="bulk_offers_toolbar_apply_title">Редактировать поле у выбранных</p>
          <select
            className="bulk_offers_select bulk_offers_select_field"
            value={fieldKey}
            onChange={(e) => setFieldKey(e.target.value)}
          >
            <option value="">— Выберите поле —</option>
            {editableFields.map((item) => (
              <option key={item.key} value={item.key}>{item.label}</option>
            ))}
          </select>
          <MyButton
            text={`Применить к выбранным (${stats.selected})`}
            onClick={handleApply}
            disabled={!canApply}
            className="bulk_offers_toolbar_apply_button"
          />
        </div>

        {lockedFields.length ? (
          <p className="bulk_offers_field_note">
            {`Закреплено режимом и не редактируется: ${lockedFields.map((item) => item.label).join(', ')}.`}
          </p>
        ) : null}

        {field ? (
          <div className="bulk_offers_toolbar_apply_editor">
            <OfferFieldEditor
              field={field}
              value={value}
              onChange={(nextValue, nextExtra) => {
                setValue(nextValue);
                setExtra(nextExtra || null);
              }}
              categories={categories}
              countries={countries}
              languages={languages}
              config={config}
              categoryForSubCategories={sharedCategory}
              countryIdForCities={sharedCountryId}
            />

            {field.type === FIELD_TYPES.templatePhotos ? (
              <div className="bulk_offers_field_inline bulk_offers_field_inline_center">
                <MyCheckBox
                  value={randomPhotos}
                  onClick={() => setRandomPhotos((prev) => !prev)}
                  text="Раздать случайно, а не все каждому"
                  wrapperClassName="bulk_offers_checkbox_item"
                />
                <select
                  className="bulk_offers_select bulk_offers_select_narrow"
                  value={photosPerOffer}
                  onChange={(e) => setPhotosPerOffer(Number(e.target.value))}
                  disabled={!randomPhotos}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((count) => (
                    <option key={count} value={count}>{count}</option>
                  ))}
                </select>
              </div>
            ) : null}

            {!stats.selected ? (
              <p className="bulk_offers_field_note">Сначала отметьте карточки чекбоксом.</p>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default BulkToolbar;
